import React from 'react';
import { motion } from 'framer-motion';
import {
  Code2,
  Globe,
  Smartphone,
  Cloud,
  LineChart,
  Brain,
  Rocket,
  TestTube2,
  LayoutGrid,
  Palette
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const services = [
  {
    icon: Code2,
    title: 'Custom Application Development',
    description: 'Bespoke software solutions tailored to your unique business needs and challenges.',
    features: [
      'Full-stack development (Frontend + Backend)',
      'API development & integrations',
      'High-performance, scalable architecture',
      'AI-powered automation workflows',
      'Secure and GDPR-compliant solutions',
      'Multi-platform support (Web, Desktop, IoT)',
      'Continuous maintenance & optimization',
      'Rapid prototyping & MVP development'
    ],
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Globe,
    title: 'Web Development',
    description: 'Dynamic and scalable web solutions that deliver impact.',
    features: [
      'Custom web applications & platforms',
      'Progressive Web Apps (PWA)',
      'Performance-driven UI development',
      'CMS & eCommerce platforms',
      'Secure & scalable backend architecture',
      'SEO & accessibility best practices',
      'API & third-party service integrations',
      'Responsive & mobile-friendly designs'
    ],
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Building seamless and intuitive mobile-first experiences.',
    features: [
      'Native iOS & Android development',
      'Cross-platform apps (Flutter, React Native)',
      'Secure user authentication & data protection',
      'Real-time synchronization & offline capabilities',
      'High-performance UI/UX optimization',
      'API integrations & push notifications',
      'App Store & Play Store compliance',
      'AI-driven personalization & recommendation systems'
    ],
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Cloud,
    title: 'Cloud Engineering',
    description: 'Scalable cloud architectures for performance and efficiency.',
    features: [
      'Cloud migration & deployment (AWS, Azure, GCP)',
      'Serverless & microservices architectures',
      'Cloud security & compliance (SOC2, GDPR)',
      'Cost-optimization & auto-scaling',
      'DevOps automation (CI/CD, IaC)',
      'Kubernetes & Docker containerization',
      'Disaster recovery & backup strategies',
      'Multi-cloud & hybrid cloud strategies'
    ],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: LineChart,
    title: 'Technology Consulting',
    description: 'Expert guidance to navigate the digital landscape and drive innovation.',
    features: [
      'Digital transformation strategy',
      'Technology stack assessment & recommendations',
      'Enterprise architecture planning',
      'Cloud adoption roadmap',
      'Security & compliance consulting',
      'Performance optimization',
      'Technology vendor selection',
      'Team augmentation & training'
    ],
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Brain,
    title: 'AI/ML Services',
    description: 'Harnessing AI & machine learning to drive innovation.',
    features: [
      'AI-powered automation & process optimization',
      'Natural language processing (NLP) & chatbots',
      'Predictive analytics & recommendation engines',
      'Computer vision & image recognition',
      'AI-driven fraud detection & risk analysis',
      'Custom ML model development & training',
      'Deep learning & neural networks',
      'Generative AI for content & automation'
    ],
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: LayoutGrid,
    title: 'Enterprise Solutions',
    description: 'Transforming enterprises with scalable and intelligent business solutions that drive innovation and growth.',
    features: [
      'ERP & CRM implementation (Microsoft Dynamics, SAP, Salesforce)',
      'Business process automation',
      'Enterprise-grade system integrations',
      'Supply chain & logistics solutions',
      'Custom enterprise applications & portals',
      'AI-driven analytics for decision-making',
      'Compliance & security frameworks',
      'Scalable cloud-based enterprise solutions'
    ],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Palette,
    title: 'Product Design',
    description: 'We craft seamless user experiences with design-led innovation that puts your users first.',
    features: [
      'UI/UX research & strategy',
      'High-fidelity prototyping & wireframing',
      'Branding & design systems',
      'Motion design & interactive animations',
      'Component-driven UI development',
      'Behavioral psychology-driven UX',
      'Accessibility & inclusive design',
      'AI-assisted UI/UX enhancements'
    ],
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: Rocket,
    title: 'Digital Transformation',
    description: 'Future-proofing businesses with innovative technology solutions.',
    features: [
      'Legacy system modernization',
      'Cloud adoption & digital infrastructure',
      'Business process re-engineering',
      'AI & automation for efficiency',
      'IoT & smart technology integrations',
      'Enterprise mobility solutions',
      'Digital product lifecycle management',
      'Cybersecurity & compliance transformation'
    ],
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1200&h=900'
  },
  {
    icon: TestTube2,
    title: 'Quality Assurance & Testing',
    description: 'Ensuring flawless user experiences with robust testing frameworks.',
    features: [
      'Manual & automated testing solutions',
      'Functional, performance & security testing',
      'API & integration testing',
      'Cross-browser & mobile compatibility testing',
      'Test-driven development (TDD) practices',
      'AI-assisted test automation',
      'Continuous testing in DevOps pipelines',
      'Compliance & regulatory testing'
    ],
    image: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&q=80&w=1200&h=900'
  }
];

const ServiceSection = ({ service, index }: { service: typeof services[0], index: number }) => {
  const Icon = service.icon;
  const isEven = index % 2 === 0;
  
  return (
    <motion.div
      id={service.title.toLowerCase().replace(/\s+/g, '-')}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      className="py-24 first:pt-0 last:pb-0 relative scroll-mt-24"
    >
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -right-20 top-20 w-40 h-40 rounded-full bg-accent/5 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute -left-20 bottom-20 w-40 h-40 rounded-full bg-accent/5 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
        {/* Content Column - Always rendered first in DOM for accessibility */}
        <div className={cn(
          "flex flex-col justify-center",
          !isEven && "lg:order-2"
        )}>
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0"
            >
              <Icon className="w-8 h-8 text-accent" />
            </motion.div>
            <h2 className="text-3xl font-display font-semibold text-white">
              {service.title}
            </h2>
          </div>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            {service.description}
          </p>

          <ul className="space-y-4">
            {service.features.map((feature, idx) => (
              <motion.li
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.1 * idx }}
                className="flex items-start gap-3 group"
              >
                <motion.div
                  whileHover={{ scale: 1.5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="w-2 h-2 rounded-full bg-accent mt-2.5 flex-shrink-0 group-hover:bg-accent/80"
                />
                <span className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                  {feature}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Image Column */}
        <div className={cn(
          "flex items-center",
          !isEven && "lg:order-1"
        )}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-black/20 z-10" />
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const ServicesGrid = () => {
  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(30,184,153,0.05)_0%,_transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4">
        <div className="divide-y divide-white/10">
          {services.map((service, index) => (
            <ServiceSection
              key={service.title}
              service={service}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;